/**
 * Managed-services contract recommendation - the PURE math, shared by the API
 * (questionnaire-load defaults) and the web client (live recalculation while the
 * user edits the Contract form). Policy resolution (workspace config /
 * calculator profile) stays server-side in
 * apps/api/src/services/sales/contract-recommendation.ts; everything here is
 * side-effect-free and unit-testable.
 *
 * Model: estimate line items classify by effort into tickets (up to and
 * INCLUDING the boundary, default 40h) and initiatives (strictly above it).
 * The generated estimate covers only the DISCOVERED scope (whatever the
 * uploaded transcripts surfaced), so the client is asked for the EXPECTED
 * TOTALS: how many tickets and initiatives IN TOTAL (including the discovered
 * ones) they expect PER YEAR of the contract. The monthly block is that yearly
 * workload's run rate:
 *
 *   yearly hours = expected total tickets/yr     x avg discovered ticket hours
 *                + expected total initiatives/yr x avg discovered initiative hours
 *   hours/month  = yearly hours / 12   (UNCLAMPED - scales with the calculation)
 *
 * Because the totals are per year, the monthly block is INDEPENDENT of the
 * contract term: a shorter contract covers proportionally less of the year at
 * the same monthly pace. The recommended term is `default_term_months` capped
 * at `max_term_months` (both 12); the user may choose less as fits the need.
 * Expected items are sized at the deal's ACTUAL average bucket hours; the
 * configured efforts (ticket 20 / initiative 60) are ONLY the fallback when a
 * bucket has no estimated items to average - they are separate from the
 * classification boundary (`ticket_max_hours`, 40). The rate is the estimate's
 * blended cost/hours (discovered scope).
 */
/** Coerce a questionnaire/number value ("25,000", 25000) to a positive number, else null. */
export function coercePositiveNumber(value) {
    const n = typeof value === 'number'
        ? value
        : typeof value === 'string' && value.trim()
            ? Number.parseFloat(value.replace(/,/g, ''))
            : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
}
/**
 * Coerce a count answer to a non-negative integer, else null. Unlike
 * {@link coercePositiveNumber}, an entered `0` is meaningful here ("we expect no
 * more tickets") and must be respected.
 */
export function coerceCount(value) {
    const n = typeof value === 'number'
        ? value
        : typeof value === 'string' && value.trim()
            ? Number.parseFloat(value.replace(/,/g, ''))
            : NaN;
    return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
}
/** The synthetic AI-mode PM section - overhead, never a scope item (see buildAiProjectManagementSection). */
const PROJECT_MANAGEMENT_SECTION_KEY = 'project_management';
/**
 * Classify the estimate's costed line items by effort - a ticket up to and
 * INCLUDING the boundary, an initiative strictly above it (default boundary
 * 40h: a 40h item is a ticket, a 40.5h item an initiative) - and total each
 * bucket's ACTUAL estimated hours (expected-scope items are sized from these
 * averages). Runs over EFFECTIVE hours (callers pass override-folded sections),
 * skipping the synthetic Project Management section (overhead, not a scope
 * item), `mentioned`-status items (0h context) and anything without positive
 * hours.
 */
export function countEstimateItemsByEffort(sections, ticketMaxHours) {
    const counts = {
        ticketCount: 0,
        initiativeCount: 0,
        ticketHours: 0,
        initiativeHours: 0,
    };
    for (const section of sections ?? []) {
        if (section.sectionKey === PROJECT_MANAGEMENT_SECTION_KEY)
            continue;
        for (const item of section.subsections ?? []) {
            if (item.scopeMeta?.scopeStatus === 'mentioned')
                continue;
            if (!(typeof item.hours === 'number' && Number.isFinite(item.hours) && item.hours > 0))
                continue;
            if (item.hours > ticketMaxHours) {
                counts.initiativeCount += 1;
                counts.initiativeHours += item.hours;
            }
            else {
                counts.ticketCount += 1;
                counts.ticketHours += item.hours;
            }
        }
    }
    return counts;
}
/**
 * Recommend the retainer terms. Yearly workload = the client's expected TOTAL
 * tickets/initiatives per year, each sized at the deal's ACTUAL average bucket
 * hours (configured efforts when a bucket has nothing to average). The
 * recommended monthly block is the yearly run rate (`yearly / 12`), rounded UP
 * to the increment - UNCLAMPED, it scales purely with the calculation. The
 * recommended term is `defaultTermMonths` capped at `maxTermMonths` (both 12 by
 * default) and is independent of the monthly block - per-year totals mean a
 * shorter contract covers less of the year at the same pace. No totals entered
 * = no monthly-block recommendation. The rate is the estimate's blended rate
 * (cost / hours), rounded to whole dollars, whenever hours + cost are known.
 */
export function computeContractRecommendation(inputs, policy) {
    const out = {};
    const { projectHours, projectCost } = inputs;
    out.recommended_term_months = Math.max(1, Math.min(Math.round(policy.defaultTermMonths), Math.round(policy.maxTermMonths)));
    if (inputs.expectedTotalTickets !== null || inputs.expectedTotalInitiatives !== null) {
        const yearlyHours = (inputs.expectedTotalTickets ?? 0) *
            (inputs.avgTicketHours ?? policy.ticketEffortHours) +
            (inputs.expectedTotalInitiatives ?? 0) *
                (inputs.avgInitiativeHours ?? policy.initiativeEffortHours);
        if (yearlyHours > 0) {
            const inc = policy.hoursPerMonthIncrement;
            out.recommended_hours_per_month = Math.ceil(yearlyHours / 12 / inc) * inc;
        }
    }
    if (projectHours !== null && projectCost !== null && projectCost > 0) {
        out.recommended_rate_per_hour = Math.round(projectCost / projectHours);
    }
    return out;
}
//# sourceMappingURL=contract-recommendation.js.map